import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { normalizarTexto } from '../utils/texto';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Upload, X, Loader2, PenLine, User, Search } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import { withAuthToken } from './gh';

const FirmasView = () => {
    const [firmas, setFirmas] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refresh, setRefresh] = useState(0);

    const [showModal, setShowModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Formulario de carga
    const [empleadoId, setEmpleadoId] = useState('');
    const [empleadoQuery, setEmpleadoQuery] = useState('');
    const [nombreCargo, setNombreCargo] = useState('');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);

    const fetchFirmas = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/firmas');
            setFirmas(res.data);
            setRefresh(r => r + 1);
        } catch (err) {
            toast.error('No se pudieron cargar las firmas');
        }
    }, []);

    const fetchEmpleados = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/empleados');
            setEmpleados(res.data);
        } catch (err) {
            // silencioso: sin lista no se puede cargar firma, se avisa al intentar
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([fetchFirmas(), fetchEmpleados()]);
            setLoading(false);
        })();
    }, [fetchFirmas, fetchEmpleados]);

    // Empleados que aún no tienen firma, filtrados por la búsqueda
    const fichasConFirma = useMemo(() => new Set(firmas.map(f => f.empleado_id)), [firmas]);
    const empleadosFiltrados = useMemo(() => {
        const q = normalizarTexto(empleadoQuery).trim();
        if (!q) return [];
        return empleados
            .filter(e => normalizarTexto(e.nombre).includes(q) || String(e.ficha || '').includes(q))
            .slice(0, 8);
    }, [empleadoQuery, empleados]);

    const resetForm = () => {
        setEmpleadoId('');
        setEmpleadoQuery('');
        setNombreCargo('');
        setFile(null);
        setPreview(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };

    const cerrarModal = () => { setShowModal(false); resetForm(); };

    const seleccionarEmpleado = (e) => {
        setEmpleadoId(e.id);
        setEmpleadoQuery(e.nombre);
        if (!nombreCargo && e.cargo) setNombreCargo(e.cargo);
    };

    const onFile = (ev) => {
        const f = ev.target.files[0];
        ev.target.value = '';
        if (!f) return;
        if (!f.type.startsWith('image/')) { toast.error('La firma debe ser una imagen'); return; }
        if (f.size > 5 * 1024 * 1024) { toast.error('La imagen no puede superar 5 MB'); return; }
        setFile(f);
        setPreview(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(f); });
    };

    const handleUpload = async () => {
        if (!empleadoId) { toast.error('Selecciona un empleado'); return; }
        if (!file) { toast.error('Adjunta la imagen de la firma'); return; }
        try {
            setSubmitting(true);
            const fd = new FormData();
            fd.append('empleado_id', empleadoId);
            fd.append('nombre_cargo', nombreCargo);
            fd.append('imagen', file);
            await axiosInstance.post('/api/firmas', fd);
            toast.success(fichasConFirma.has(empleadoId) ? 'Firma reemplazada' : 'Firma guardada');
            cerrarModal();
            fetchFirmas();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo guardar la firma');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleActiva = async (firma) => {
        try {
            await axiosInstance.put(`/api/firmas/${firma.id}`, { activa: !firma.activa });
            setFirmas(prev => prev.map(f => f.id === firma.id ? { ...f, activa: !f.activa } : f));
        } catch (err) {
            toast.error('No se pudo cambiar el estado');
        }
    };

    const eliminar = async (firma) => {
        if (!window.confirm(`¿Eliminar la firma de ${firma.empleado_nombre}?`)) return;
        try {
            await axiosInstance.delete(`/api/firmas/${firma.id}`);
            toast.success('Firma eliminada');
            setFirmas(prev => prev.filter(f => f.id !== firma.id));
        } catch (err) {
            toast.error('No se pudo eliminar');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 size={32} className="animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Firmas digitales</h1>
                    <p className="text-gray-500 mt-1">Firmas que se estampan en las constancias de trabajo</p>
                </div>
                <button
                    onClick={() => { resetForm(); setShowModal(true); }}
                    className="px-6 py-3 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
                >
                    <Plus size={18} />
                    Cargar firma
                </button>
            </div>

            {/* Lista */}
            {firmas.length === 0 ? (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-12 text-center">
                    <PenLine size={40} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 font-medium">Todavía no hay firmas cargadas</p>
                    <p className="text-gray-400 text-sm mt-1">Carga la firma de un coordinador para empezar</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {firmas.map(firma => (
                        <motion.div
                            key={firma.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-gray-900 truncate">{firma.empleado_nombre}</p>
                                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                                        {firma.nombre_cargo}
                                        {firma.empleado_sede && <span className="text-gray-400"> · {firma.empleado_sede}</span>}
                                    </p>
                                    {firma.empleado_ficha && <p className="text-xs text-gray-400 mt-0.5">Ficha {firma.empleado_ficha}</p>}
                                </div>
                                <button
                                    onClick={() => eliminar(firma)}
                                    className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                                    title="Eliminar firma"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>

                            {/* Imagen de la firma */}
                            <div className="mt-3 h-24 rounded-xl border border-gray-100 bg-gray-50 flex items-center justify-center overflow-hidden">
                                <img
                                    src={withAuthToken(`/api/firmas/${firma.id}/imagen`) + `&v=${refresh}`}
                                    alt={`Firma de ${firma.empleado_nombre}`}
                                    className="max-h-full max-w-full object-contain"
                                />
                            </div>

                            {/* Estado */}
                            <div className="mt-4 flex items-center justify-between">
                                <span className={`text-xs font-medium px-2.5 py-1 rounded-lg ${
                                    firma.activa ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'
                                }`}>
                                    {firma.activa ? 'Activa' : 'Inactiva'}
                                </span>
                                <button
                                    onClick={() => toggleActiva(firma)}
                                    className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    {firma.activa ? 'Desactivar' : 'Activar'}
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Modal de carga */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                        onClick={cerrarModal}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.96, y: 10 }}
                            className="bg-white rounded-3xl shadow-xl w-full max-w-lg p-8 space-y-6"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-900">Cargar firma</h2>
                                <button onClick={cerrarModal} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Empleado */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Empleado</label>
                                {empleadoId ? (
                                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 flex items-center gap-2">
                                        <User size={16} className="text-gray-400" />
                                        <span className="flex-1">{empleadoQuery}</span>
                                        <button onClick={() => { setEmpleadoId(''); setEmpleadoQuery(''); }} className="text-gray-400 hover:text-gray-600">
                                            <X size={15} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-gray-900">
                                            <Search size={16} className="text-gray-400" />
                                            <input
                                                type="text"
                                                value={empleadoQuery}
                                                onChange={e => setEmpleadoQuery(e.target.value)}
                                                placeholder="Buscar por nombre o ficha..."
                                                className="flex-1 bg-transparent outline-none text-sm"
                                            />
                                        </div>
                                        {empleadosFiltrados.length > 0 && (
                                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto">
                                                {empleadosFiltrados.map(e => (
                                                    <button
                                                        key={e.id}
                                                        onClick={() => seleccionarEmpleado(e)}
                                                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors flex items-center justify-between gap-2"
                                                    >
                                                        <span className="text-sm text-gray-800 truncate">{e.nombre}</span>
                                                        <span className="text-xs text-gray-400 flex-shrink-0">
                                                            {e.sede || ''}{fichasConFirma.has(e.id) ? ' · ya tiene' : ''}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Cargo */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Cargo (aparece bajo la firma)</label>
                                <input
                                    type="text"
                                    value={nombreCargo}
                                    onChange={e => setNombreCargo(e.target.value)}
                                    placeholder="Ej: Coordinador de Gestión Humana"
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none text-sm"
                                />
                            </div>

                            {/* Imagen */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Imagen de la firma</label>
                                {preview ? (
                                    <div className="relative">
                                        <div className="h-28 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                                            <img src={preview} alt="Vista previa" className="max-h-full max-w-full object-contain" />
                                        </div>
                                        <button
                                            onClick={() => { setPreview(prev => { if (prev) URL.revokeObjectURL(prev); return null; }); setFile(null); }}
                                            className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-lg text-gray-500 hover:text-red-500 shadow-sm"
                                        >
                                            <X size={15} />
                                        </button>
                                    </div>
                                ) : (
                                    <label className="flex flex-col items-center justify-center gap-2 h-28 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-gray-300 transition-colors">
                                        <Upload size={20} className="text-gray-400" />
                                        <span className="text-sm text-gray-500">Subir imagen (PNG, JPG o WEBP)</span>
                                        <input type="file" accept="image/*" className="hidden" onChange={onFile} />
                                    </label>
                                )}
                                <p className="text-xs text-gray-400 mt-2">Recomendado: PNG con fondo transparente, máximo 5 MB.</p>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button onClick={cerrarModal} className="flex-1 py-3 text-gray-600 font-medium hover:bg-gray-100 rounded-2xl transition-colors">
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleUpload}
                                    disabled={submitting || !empleadoId || !file}
                                    className="flex-1 py-3 bg-gray-900 text-white font-medium rounded-2xl hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {submitting ? <Loader2 size={18} className="animate-spin" /> : <PenLine size={18} />}
                                    Guardar firma
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FirmasView;
